"use client";

import React, { useMemo } from "react";
import { Grid, Card, CardContent, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import Chart from "react-apexcharts";

const mergeOpts = (base = {}, extra = {}) => {
  const merged = {
    ...base,
    ...extra,
    xaxis: { ...base.xaxis, ...extra.xaxis },
    yaxis: { ...base.yaxis, ...extra.yaxis },
    grid: { ...base.grid, ...extra.grid },
    legend: { ...base.legend, ...extra.legend },
    dataLabels: { ...base.dataLabels, ...extra.dataLabels },
    stroke: { ...base.stroke, ...extra.stroke },
    fill: { ...base.fill, ...extra.fill },
    tooltip: { ...base.tooltip, ...extra.tooltip },
    plotOptions: {
      ...base.plotOptions,
      bar: {
        ...(base.plotOptions?.bar || {}),
        ...(extra.plotOptions?.bar || {}),
      },
    },
    responsive: extra.responsive || base.responsive,
  };

  merged.chart = {
    ...merged.chart,
    toolbar: { show: false, tools: { download: false } },
    zoom: { enabled: false },
  };

  return merged;
};

const ChartsSection = ({
  incomeVsExpensesOptions,
  incomeVsExpensesSeries,
  profitTrendOptions,
  profitTrendSeries,
  currency
}) => {
  const isDark = useTheme().palette.mode === "dark";

  const colors = ["#56C662", "#ef4444", "#2563eb", "#f59e0b"];
  const baseNeat = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        foreColor: isDark ? "#cbd5e1" : "#334155",
      },
      grid: {
        borderColor: alpha(isDark ? "#94a3b8" : "#334155", 0.12),
        strokeDashArray: 3,
        padding: { left: 8, right: 8 },
      },
      dataLabels: { enabled: false },
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "right",
        fontSize: "12px",
        markers: { radius: 12 },
        itemMargin: { horizontal: 8 },
      },
      xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: "12px" } },
      },
      yaxis: {
        labels: {
          style: { fontSize: "12px" },
          formatter: (v) =>
            Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`,
        },
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
        x: { show: true },
        y: {
          formatter: (v) =>
            typeof v === "number"
              ? `${currency} ${Math.round(v).toLocaleString("en-PK")}`
              : v,
        },
      },
      colors,
      responsive: [
        {
          breakpoint: 900,
          options: {
            legend: { position: "bottom", horizontalAlign: "center" },
            grid: { padding: { left: 4, right: 4 } },
          },
        },
      ],
    }),
    [isDark, currency]
  );

  const barDefaults = useMemo(
    () =>
      mergeOpts(baseNeat, {
        chart: { stacked: false },
        plotOptions: {
          bar: {
            columnWidth: "30px",
            barHeight: "100%",
            borderRadius: 6,
            borderRadiusApplication: "end",
            distributed: false,
          },
        },
      }),
    [baseNeat]
  );

  const lineDefaults = useMemo(
    () =>
      mergeOpts(baseNeat, {
        stroke: {
          curve: "smooth",
          width: 3,
          colors: ["#10b981"],
        },
        markers: {
          size: 4,
          strokeWidth: 2,
          colors: [isDark ? "#1c212b" : "#fff"],
          strokeColors: "#10b981",
          hover: { size: 6 },
        },
        fill: {
          type: "solid",
          colors: ["#10b981"],
        },
      }),
    [baseNeat]
  );

  const incomeVsExpensesMerged = useMemo(
    () => mergeOpts(barDefaults, incomeVsExpensesOptions || {}),
    [barDefaults, incomeVsExpensesOptions]
  );

  const profitTrendMerged = useMemo(
    () => mergeOpts(lineDefaults, profitTrendOptions || {}),
    [lineDefaults, profitTrendOptions]
  );

  const cardSx = {
    p: 0,
    borderRadius: 2,
    border: (t) => `2px solid ${alpha("#10b981", 0.18)}`,
    bgcolor: (t) => t.palette.background.paper,
    transition:
      "box-shadow .2s ease, transform .2s ease, border-color .2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: 3,
      borderColor: (t) => alpha("#10b981", 0.3),
    },
  };

  return (
    <Grid container spacing={2} sx={{ p: 0, mt: 5 }}>
      <Grid xs={12} md={6}>
        <Card elevation={0} sx={cardSx}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              mb={1}
              sx={{ color: isDark ? "#7ee2a8" : "#007a52" }}
            >
              Income vs Expenses
            </Typography>
            <Chart
              options={incomeVsExpensesMerged}
              series={incomeVsExpensesSeries}
              type="bar"
              height={300}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid xs={12} md={6}>
        <Card elevation={0} sx={cardSx}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              mb={1}
              sx={{ color: isDark ? "#7ee2a8" : "#007a52" }}
            >
              Profit Trend
            </Typography>
            <Chart
              options={profitTrendMerged}
              series={profitTrendSeries}
              type="line"
              height={300}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ChartsSection;
